import http from "../utils/http-common";
import { CreateHeatingValue, FilterSetupHeatingValue, UpdateHeatingValue } from "src/entity/flare/heating-value.entity";
import { ClearReportValueBody, CreateFlareVolume, FilterSetupFlareVolume, UpdateFlareVolume } from "src/entity/flare/flare-volume.entity";
import { FilterManualFlareVolume, UpdateManualFlareVolume } from "src/entity/flare/manual-flare-volume.entity";
import {
  CalculateReportAfterRejectApproval,
  CalculateReportAfterUpdateMasterdataBody,
  FilterReportFlareVolume,
} from "src/entity/flare/report-flare-volume.entity";
import { FilterReportVocEmission } from "src/entity/flare/report-voc-emission.entity";
import { FilterColumnProps } from "src/entity/common/query.entity";
import { SubmitReportVocBody } from "src/entity/common/global.entity";

const basePath = "/api/flare";
const baseMasterPath = "/api/masterdata";
const timeoutCalculation = 600000; // 10 minutes

// Flare Setup Heating Value
export const getYearHeatingValue = (companyId: string) => {
  return http.get(`${basePath}/heating-value/year/${companyId}`);
};
export const getAllSettingFlareCompound = (filter?: { isActive: boolean }) => {
  return http.get(`${baseMasterPath}/setting-flare-compound`, { params: filter || {} });
};

export const createSetupHeatingValue = (body: CreateHeatingValue) => {
  return http.post(`${basePath}/heating-value`, body);
};
export const updateSetupHeatingValue = (data: UpdateHeatingValue) => {
  return http.patch(`${basePath}/heating-value`, data);
};
export const deleteSetupHeatingValue = (_id: string) => {
  return http.delete(`${basePath}/heating-value/${_id}`);
};
export const searchSetupHeatingValue = (data: FilterSetupHeatingValue) => {
  return http.post(`${basePath}/search/heating-value`, data);
};
export const getFilterSetupHeatingValue = (params: FilterColumnProps) => {
  return http.get(`${basePath}/heating-value/filter-column-flare`, { params });
};

// Flare Setup Flare Volume
export const getYearFlareVolume = (companyId: string) => {
  return http.get(`${basePath}/flare-volume/year/${companyId}`);
};
export const getAllSettingFlareInformation = (filter: { isActive: boolean }) => {
  return http.get(`${baseMasterPath}/setting-flare-info/all`, { params: filter });
};
export const getAllHeatingValue = (year: string) => {
  return http.get(`${basePath}/heating-value/all`, { params: { year } });
};

export const createSetupFlareVolume = (body: CreateFlareVolume) => {
  return http.post(`${basePath}/flare-volume`, body);
};
export const updateSetupFlareVolume = (data: UpdateFlareVolume) => {
  return http.patch(`${basePath}/flare-volume`, data);
};
export const deleteSetupFlareVolume = (_id: string) => {
  return http.delete(`${basePath}/flare-volume/${_id}`);
};
export const searchSetupFlareVolume = (data: FilterSetupFlareVolume) => {
  return http.post(`${basePath}/search/flare-volume`, data);
};
export const getFilterSetupFlareVolume = (params: FilterColumnProps) => {
  return http.get(`${basePath}/flare-volume/filter-column-flare`, { params });
};

// Manual Flare Volume
export const searchManualFlareVolume = (data: FilterManualFlareVolume) => {
  return http.post(`${basePath}/search/manual-flare-volume`, data);
};
export const updateManualFlareVolume = (data: UpdateManualFlareVolume) => {
  return http.patch(`${basePath}/manual-flare-volume`, data);
};
export const getFilterManualFlareVolume = (params: FilterColumnProps) => {
  return http.get(`${basePath}/manual-flare-volume/filter-column`, { params });
};

// Report Flare Volume
export const searchReportFlareVolume = (data: FilterReportFlareVolume) => {
  return http.post(`${basePath}/search/report-flare-volume`, data);
};
export const getFilterReportFlareVolume = (params: FilterColumnProps) => {
  return http.get(`${basePath}/report-flare-volume/filter-column`, { params });
};

// Report VOC Emission
export const searchReportVocEmission = (data: FilterReportVocEmission) => {
  return http.post(`${basePath}/search/report-voc-emission`, data);
};
export const getFilterReportVocEmission = (params: FilterColumnProps) => {
  return http.get(`${basePath}/report-voc-emission/filter-column`, { params });
};

// Calculate Report — API returns immediately; calculation continues in background (reportFlareVolumeStatus)
export const calculateReportWhenUpdateOrCreate = (flareId: string) => {
  return http.post(`${basePath}/calculation/report-flare-volume/${flareId}`, {});
};

export const getOptionMonthSubmit = (year: string, companyId?: string) => {
  const url = companyId
    ? `${basePath}/report/voc-emission/approve/month/${year}?companyId=${companyId}`
    : `${basePath}/report/voc-emission/approve/month/${year}`;
  return http.get(url);
  // return http.get(`${basePath}/report/voc-emission/approve/month/${year}`);
};

export const submitReportVoc = (data: SubmitReportVocBody) => {
  return http.post(`${basePath}/report/voc-emission/approve/submit-report`, data);
};

export const getInfoStepApprove = (approveId: string) => {
  return http.get(`${basePath}/report/voc-emission/approve/info-step/${approveId}`);
};

export const getOptionVocApprove = () => {
  return http.get(`${basePath}/report/voc-emission/approve/filter-voc-approve`);
};

export const updateApproveStep = (data: any) => {
  return http.patch(`${basePath}/report/voc-emission/approve/approve-step`, data);
};

export const getAllFilterReportGovernment = () => {
  return http.get(`${basePath}/report/government/all-filter`);
};

export const getReportGovernment = (params: { year: string | null; month: string | null; flareName: string | null }) => {
  return http.get(`${basePath}/report/government`, { params });
};

export const calculateReportAfterRejectApproval = (body: CalculateReportAfterRejectApproval) => {
  return http.post(`${basePath}/calculation/report/action-reject`, body, { timeout: timeoutCalculation });
};

// Re Calculate Report After Update Masterdata Flare Information
export const calculateReportAfterUpdateMasterFalreInfo = (body: CalculateReportAfterUpdateMasterdataBody) => {
  return http.patch(`${basePath}/report/calculation/after-update/masterdata`, body, { timeout: timeoutCalculation });
};
export const calculateReportAfterUpdateMasterFlareCompound = (_id: string) => {
  return http.patch(`${basePath}/report/calculation/after-update/masterdata/flare-compound/${_id}`, { timeout: timeoutCalculation });
};

export const clearReportValueByGasName = (body: ClearReportValueBody) => {
  return http.patch(`${basePath}/clear/all-report`, body);
};

export const SettingService = {
  getYearHeatingValue,
  createSetupHeatingValue,
  updateSetupHeatingValue,
  deleteSetupHeatingValue,
  searchSetupHeatingValue,
  getFilterSetupHeatingValue,
  getAllSettingFlareCompound,
  getYearFlareVolume,
  getAllSettingFlareInformation,
  createSetupFlareVolume,
  updateSetupFlareVolume,
  deleteSetupFlareVolume,
  searchSetupFlareVolume,
  getFilterSetupFlareVolume,
  searchManualFlareVolume,
  updateManualFlareVolume,
  searchReportFlareVolume,
  getFilterManualFlareVolume,
  searchReportVocEmission,
  getFilterReportFlareVolume,
  getFilterReportVocEmission,
  calculateReportWhenUpdateOrCreate,
  getOptionMonthSubmit,
  submitReportVoc,
  getInfoStepApprove,
  getOptionVocApprove,
  updateApproveStep,
  calculateReportAfterUpdateMasterFalreInfo,
  clearReportValueByGasName,
  calculateReportAfterRejectApproval,
  calculateReportAfterUpdateMasterFlareCompound,
};
